import { useMemo } from "react";
import { Dropdown, MenuProps } from "antd";
import {
  ArrowLeft,
  Save,
  Settings,
  ChevronDown,
  Edit,
  Loader2,
  Globe,
  Banknote,
  Trash2,
  Mail,
  FileDown,
} from "lucide-react";

type Mode = "create" | "view" | "edit";

interface InvoiceManagerHeaderProps {
  mode: Mode;

  invoice: {
    invoiceNumber?: string | null;
    hasError: boolean;
    isLoading: boolean;
  };

  company: {
    companies: { id: number; companyName: string }[];
    selectedCompanyId: number | null;
    loadingCompanies: boolean;
    onChangeCompany: (id: number) => void;
  };

  settings: {
    currency?: string;
    language?: string;
    onChangeLanguage: (lang: string) => void;
    onChangeCurrency: (curr: string) => void;
  };

  state: {
    isSavingInvoice: boolean;
    canCreateInvoice: boolean;
  };

  actions: {
    onBack: () => void;
    onEdit: () => void;
    onSave: () => void;

    onSendEmail: () => void;
    onDownloadPDF: () => void;
    onDelete: () => void;
  };
}

export const InvoiceManagerHeader: React.FC<InvoiceManagerHeaderProps> = ({
  invoice,
  mode,
  company,
  settings,
  state,
  actions,
}) => {
  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const {
    invoiceNumber,
    hasError: hasErrorFetchingInvoice,
    isLoading: isLoadingInvoice,
  } = invoice;
  const successfullyFetchedInvoice =
    !isLoadingInvoice && !hasErrorFetchingInvoice;

  const { companies, selectedCompanyId, onChangeCompany } = company;
  const { currency, language, onChangeLanguage, onChangeCurrency } = settings;

  const companyMenuItems = useMemo<MenuProps["items"]>(
    () =>
      companies.map((c) => ({
        key: c.id,
        label: c.companyName,
        onClick: () => onChangeCompany(c.id),
      })),
    [companies, onChangeCompany],
  );

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  let titleText = "";

  if (isCreateMode) titleText = "New Invoice";
  else if (isEditMode) titleText = `Editing Invoice #${invoiceNumber ?? "..."}`;
  else titleText = `Invoice #${invoiceNumber ?? "..."}`;

  const canShowCreateControls = isCreateMode && state.canCreateInvoice;
  const canShowEditControls = isEditMode && successfullyFetchedInvoice;
  const canShowViewControls = isViewMode && successfullyFetchedInvoice;

  // Change invoice language and currency menu
  const settingsMenuItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "language",
        label: (
          <span className="font-semibold text-gray-500 text-xs uppercase">
            Language
          </span>
        ),
        type: "group",
        children: [
          {
            key: "lang-en",
            label: "English",
            onClick: () => onChangeLanguage("en"),
            icon: <Globe size={14} />,
          },
          {
            key: "lang-es",
            label: "Spanish",
            onClick: () => onChangeLanguage("es"),
            icon: <Globe size={14} />,
          },
        ],
      },
      { type: "divider" },
      {
        key: "currency",
        label: (
          <span className="font-semibold text-gray-500 text-xs uppercase">
            Currency
          </span>
        ),
        type: "group",
        children: [
          {
            key: "curr-usd",
            label: "USD - US Dollar",
            onClick: () => onChangeCurrency("USD"),
            icon: <Banknote size={14} />,
          },
          {
            key: "curr-pen",
            label: "PEN - Sol Peruano",
            onClick: () => onChangeCurrency("PEN"),
            icon: <Banknote size={14} />,
          },
        ],
      },
    ],
    [onChangeLanguage, onChangeCurrency],
  );

  // Actions menu
  const actionMenuItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "email",
        icon: <Mail size={16} />,
        label: "Send by Email",
        onClick: actions.onSendEmail,
      },
      {
        key: "download",
        icon: <FileDown size={16} />,
        label: "Download PDF",
        onClick: actions.onDownloadPDF,
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        icon: <Trash2 size={16} />,
        label: "Delete",
        danger: true,
        onClick: actions.onDelete,
      },
    ],
    [actions.onSendEmail, actions.onDownloadPDF, actions.onDelete],
  );

  return (
    <div className="flex items-center justify-between p-4 bg-white border-b shadow-sm sticky top-0 z-10">
      {/* Back button and title */}
      <div className="flex items-center gap-4">
        <button
          onClick={actions.onBack}
          className="btn btn-gray flex items-center"
        >
          <ArrowLeft size={16} />
          {isEditMode ? "Back to Invoice" : "Back to Invoices"}
        </button>
        {(isCreateMode || successfullyFetchedInvoice) && (
          <h2 className="text-xl font-semibold text-gray-800">{titleText}</h2>
        )}
      </div>

      {/* Company dropdown, settings dropdown, and actions dropdown */}
      <div className="flex items-center gap-2">
        {/* Company dropdown (new invoices only) */}
        {canShowCreateControls && (
          <Dropdown menu={{ items: companyMenuItems }}>
            <button className="btn btn-gray flex items-center">
              <span className="text-gray-400 text-xs">Company</span>
              <span className="font-medium truncate max-w-[140px]">
                {selectedCompany?.companyName || "Select company"}
              </span>
              <ChevronDown size={16} />
            </button>
          </Dropdown>
        )}

        {/* Settings dropdown (new invoices and edit invoices) */}
        {(canShowCreateControls || canShowEditControls) && (
          <Dropdown menu={{ items: settingsMenuItems }}>
            <button className="btn btn-gray flex items-center gap-2">
              <Settings size={16} />
              <span>
                Settings ({currency || "USD"} -{" "}
                {(language || "en").toUpperCase()})
              </span>
            </button>
          </Dropdown>
        )}

        {/* Actions dropdown and edit button (view invoices only)*/}
        {canShowViewControls && (
          <>
            {/* Actions dropdown */}
            <Dropdown menu={{ items: actionMenuItems }}>
              <button className="btn btn-gray flex items-center">
                Actions <ChevronDown size={16} />
              </button>
            </Dropdown>

            {/* Edit button */}
            <button
              onClick={actions.onEdit}
              className="btn btn-green flex items-center"
            >
              <Edit size={16} /> Edit
            </button>
          </>
        )}

        {/* Save button (new invoices and edit invoices) */}
        {(canShowCreateControls || canShowEditControls) && (
          <button
            onClick={actions.onSave}
            className="btn btn-green flex items-center"
            disabled={state.isSavingInvoice}
          >
            {state.isSavingInvoice ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            {state.isSavingInvoice
              ? "Saving..."
              : isCreateMode
                ? "Create Invoice"
                : "Save Changes"}
          </button>
        )}
      </div>
    </div>
  );
};
